/**
 * Meetings the client still has to validate (W5, portal home « RDV à
 * valider » and the menu badge; MeetingsSection may reuse it).
 *
 * A booked meeting awaits validation while its `client_validation_status` is
 * empty or `pending` and it has not taken place yet. Its time is
 * `meeting_date` when the call recorded one; the older rows only carry
 * `connected_at` (the moment the call connected), which the portal used as
 * the meeting time so far (PORTAIL-W5-1 in docs/cartography/BUGS-OUVERTS.md).
 */
export interface MeetingLike {
  meeting_date?: string | Date | null;
  connected_at?: string | Date | null;
  client_validation_status?: string | null;
}

/** When the meeting takes place, or null when the row says nothing usable. */
export function meetingTime(m: MeetingLike): Date | null {
  for (const raw of [m.meeting_date, m.connected_at]) {
    if (!raw) continue;
    const d = new Date(raw);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return null;
}

export function isAwaitingValidation(m: MeetingLike, now: Date = new Date()): boolean {
  if ((m.client_validation_status || 'pending') !== 'pending') return false;
  const at = meetingTime(m);
  return at !== null && at >= now;
}

/**
 * REV-W5-6: the one definition of « RDV à valider » for the whole portal
 * (home card, Mes rendez-vous › À valider and its count, the menu badge):
 *   - a booked meeting: `meeting_booked: true` or `call_outcome:
 *     'meeting_booked'` (server filter below);
 *   - validation empty or `pending` (server filter);
 *   - still to come (`isAwaitingValidation`, applied on the rows: the meeting
 *     time falls back on `connected_at`, which a server filter cannot do).
 * A past meeting can no longer be validated: it shows under « Tous ».
 */
export const BOOKED_MEETING_FILTER = { $or: [{ meeting_booked: true }, { call_outcome: 'meeting_booked' }] };
export const PENDING_VALIDATION_FILTER = { client_validation_status: { $in: [null, '', 'pending'] } };

/** Rows read for the count; far above what a client has pending at once. */
export const AWAITING_VALIDATION_LIMIT = 500;

export function awaitingValidationFilter(customerId?: string | null): Record<string, unknown> {
  return { $and: [BOOKED_MEETING_FILTER, PENDING_VALIDATION_FILTER, ...(customerId ? [{ customer_id: customerId }] : [])] };
}

/** The rows still to validate, soonest first. */
export function awaitingValidation<T extends MeetingLike>(rows: readonly T[], now: Date = new Date()): T[] {
  return rows
    .filter((m) => isAwaitingValidation(m, now))
    .sort((a, b) => (meetingTime(a)?.getTime() ?? 0) - (meetingTime(b)?.getTime() ?? 0));
}
